/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import BookingAction from "store/booking/BookingAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {DEFAULT_HALL_ID, IS_UPSELL_ALLOWED} from "helpers/config";
import {consoleLog, currencyNumberFormat} from "utils/utility";
import {RouteComponentProps, withRouter} from "react-router";
import BookingPackagesNewDriverRow
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesNewDriverRow";
import BookingFooterSteps from "pages/Front/Booking/components/BookingFooterSteps";
import BookingPackagesNewActionDriverRow
    from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesNewActionDriverRow";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import Skeleton from "react-loading-skeleton";
import {UserIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";

export interface BookingPackagesBasicParams {}

interface BookingPackagesBasicProps extends RouteComponentProps<BookingPackagesBasicParams> {}

const BookingPackagesBasic = (props: BookingPackagesBasicProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush} = useNavigation();

    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const settings = useTypedSelector(state => state.settingsState);
    const personsCount: number = filter.adults + filter.children;
    const [allFilled, setAllFilled] = useState<boolean>(false);
    const [isKartingDisabled, setIsKartingDisabled] = useState<boolean>(!!settings?.config?.disable_karting_tickets || false);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const {setDefaultPackagesItems, manageResetStates, getTotalPrice, getFilledCount, resetDefaultPackagesItems} = usePackageItemKartingOptions();
    const {bookWithAutomaticTimeslot} = useCustomTimeslotsNew(() => {}, props.history, [
        scrollRefTimeslots,
        scrollRefCustomTimeslots,
        scrollRefConfirmTimeslots,
    ]);

    /**
     * Fetches packages based on category and time slot.
     */
    const fetchPackagesByCategoryAndTimeSlot = () => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlot({
                adults: filter.adults,
                children: filter.children,
                date: filter.date,
                package_category_name: filter?.category ? filter.category : PackageCategory.General,
                hall_id: DEFAULT_HALL_ID,
                activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
            })
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        });
    };

    const onNextStepHandler = () => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;
        if (IS_UPSELL_ALLOWED && availablePackagesHasUpsell) {
            return historyPush(RouteName.UpsellsNew, "Packages");
        }
        bookWithAutomaticTimeslot(null, true);
    };

    useEffect(() => {
        fetchPackagesByCategoryAndTimeSlot();
    }, [filter.adults, filter.children]);

    useEffect(() => {
        if (selectedPackageItems.length === 0) {
            setDefaultPackagesItems();
        }
        if (selectedPackageItems.length > 0) {
            manageResetStates();
        }
    }, [selectedPackageItems]);

    useEffect(() => {
        setAllFilled(getFilledCount() == personsCount);
    }, [selectedPackageItems]);

    useEffect(() => {
        // TODO: pouvazovat nad tym ci to nespravit tak, ze reset bude len ak bude karting disabled
        resetDefaultPackagesItems().then(r => {});
    }, [isKartingDisabled]);

    useEffect(() => {
        setIsKartingDisabled(!!settings?.config?.disable_karting_tickets || false);
    }, [settings?.config?.disable_karting_tickets]);

    if (!availablePackageItems?.packageItems || !selectedPackageItems) {
        return (
            <div className={"BookingPackagesBasic w-full"}>
                <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                    <table className="BookingPackagesBasicPackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white">
                        <colgroup className="hidden lg:table-column-group">
                            <col className="w-auto" />
                            <col className="w-auto" />
                            <col className="w-auto" />
                            <col className="min-w-[110px] w-auto" />
                        </colgroup>
                        <thead className="hidden lg:table-header-group">
                            <tr>
                                <th className="text-left font-bold text-lg text-black"></th>
                                <th className="font-bold text-lg text-black">{t("Options")}</th>
                                <th className="font-bold text-lg text-black">{t("Select visit duration")}</th>
                                <th className="font-bold text-lg text-black">{t("Price")}</th>
                            </tr>
                        </thead>
                        <tbody className="flex flex-col lg:table-row-group">
                            <tr className="BookingPackagesNewDriverRow flex flex-col lg:table-row last-of-type:border-b-0 border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0">
                                <td className="align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0">
                                    <div className="flex flex-row items-center justify-start mb-2 sm:mb-0">
                                        <UserIcon className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2")} />
                                        <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                        <span className="font-bold text-sm xl:text-base">1. {t("Person")}</span>
                                    </div>
                                </td>
                                <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        {t("Options")}<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                                <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        Ticket nach Stunden<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                                <td className="align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        {t("Price")}<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className={"BookingPackagesBasic w-full"}>
            <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                <table
                    className={
                        "BookingPackagesBasicPackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white"
                    }
                >
                    <colgroup className={"hidden lg:table-column-group"}>
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="min-w-[110px] w-auto" />
                    </colgroup>
                    <thead className={"hidden lg:table-header-group"}>
                        <tr>
                            <th className={"text-left font-bold text-lg text-black"}></th>
                            <th className={"font-bold text-lg text-black"}>{t("Options")}</th>
                            <th className={"font-bold text-lg text-black"}>{t("Select visit duration")}</th>
                            <th className={"font-bold text-lg text-black"}>{t("Price")}</th>
                        </tr>
                    </thead>
                    <tbody className={"flex flex-col lg:table-row-group"}>
                        {selectedPackageItems.length > 0 &&
                            selectedPackageItems.map((packageItemByPerson, index) => {
                                if (
                                    filter.category === PackageCategory.Action ||
                                    filter.category === PackageCategory.School ||
                                    filter.category === PackageCategory.Kinder ||
                                    filter.category === PackageCategory.FerienPassB ||
                                    filter.category === PackageCategory.FerienPass
                                ) {
                                    return (
                                        <BookingPackagesNewActionDriverRow
                                            key={packageItemByPerson.personIndex}
                                            i={index}
                                            id={packageItemByPerson.id}
                                            index={packageItemByPerson.personIndex}
                                            personType={packageItemByPerson.person_type}
                                            category={filter.category}
                                        />
                                    );
                                } else {
                                    return (
                                        <BookingPackagesNewDriverRow
                                            key={packageItemByPerson.personIndex}
                                            i={index}
                                            index={packageItemByPerson.personIndex}
                                            personType={packageItemByPerson.person_type}
                                        />
                                    );
                                }
                            })}
                    </tbody>
                </table>
            </div>

            <div className={"flex flex-col items-end justify-center w-full my-6"}>
                <div
                    className={
                        "flex flex-col items-center justify-center rounded-md bg-white text-sm font-semibold border shadow w-full min-w-[210px] sm:w-auto"
                    }
                >
                    <h3 className={"font-bold text-xl text-black w-full px-4 py-3 border-b border-dashed flex justify-center sm:justify-end"}>
                        {t("Total price")}
                    </h3>
                    <div className={"text-base font-bold text-gray-600 w-full px-4 py-3 flex justify-center sm:justify-end"}>
                        {currencyNumberFormat(getTotalPrice())}
                    </div>
                </div>
            </div>

            <BookingFooterSteps nextStepDisabled={!allFilled} onClick={() => onNextStepHandler()} />
        </div>
    );
};

export default withRouter(BookingPackagesBasic);
